import axios from 'axios';

import { buildError } from '@aslanpay/portal-common-lib';

import type {
  BadgeStatus,
  PointsTransactionReason,
  ScheduledGift,
  ScheduledGiftRecipientOutcome,
  ScheduledGiftSource,
  ScheduledGiftStatus,
} from '@aslanpay/portal-common-lib';

// Admins pick the send time in UK time; the API stores it in UTC.
export const SCHEDULE_TIME_ZONE = 'Europe/London';
export const SCHEDULE_SLOT_MINUTES = 15;

// The API schedules only employee gifts; any other reason is refused.
export const SCHEDULED_GIFT_REASON: PointsTransactionReason = 'EMPLOYEE_GIFT';

interface StatusDisplay {
  label: string;
  badge: BadgeStatus;
}

const GIFT_STATUS_DISPLAY: Record<ScheduledGiftStatus, StatusDisplay> = {
  SCHEDULED: { label: 'Scheduled', badge: 'created' },
  PROCESSING: { label: 'Sending', badge: 'invited' },
  SENT: { label: 'Sent', badge: 'active' },
  PARTIALLY_SENT: { label: 'Partially sent', badge: 'warning' },
  FAILED: { label: 'Failed', badge: 'left' },
  CANCELLED: { label: 'Cancelled', badge: 'locked' },
};

const RECIPIENT_OUTCOME_DISPLAY: Record<ScheduledGiftRecipientOutcome, StatusDisplay> = {
  PENDING: { label: 'Pending', badge: 'created' },
  SENT: { label: 'Sent', badge: 'active' },
  SKIPPED: { label: 'Skipped', badge: 'warning' },
  FAILED: { label: 'Failed', badge: 'left' },
};

const SOURCE_LABELS: Record<ScheduledGiftSource, string> = {
  MANUAL: 'Manual',
  BIRTHDAY: 'Birthday',
  LONG_SERVICE: 'Long service',
};

const OUTCOME_REASON_LABELS: Record<string, string> = {
  LEFT: 'Left the company',
  ON_UNPAID_LEAVE: 'On unpaid leave',
  POINTS_POT_INACTIVE: 'Points pot inactive',
  INSUFFICIENT_FUNDS: 'Not enough points',
};

// Why a gift is in its status, by the code the API gives in stateReason.
const GIFT_STATE_REASON_LABELS: Record<string, string> = {
  INSUFFICIENT_FUNDS: "The company's points balance didn't cover the gift. Top up and try again.",
  NO_ELIGIBLE_RECIPIENTS: 'None of the employees could receive the gift.',
  GIFTING_DISABLED: 'Gifting was turned off for the company.',
  LEDGER_REJECTED: 'None of the employees could be paid.',
  LEDGER_UNAVAILABLE: "The points couldn't be moved at the time. Please try again shortly.",
  UNEXPECTED_ERROR: 'Something went wrong while sending the gift.',
};

// The API may add statuses, outcomes and reasons later: anything unknown is shown as its raw value, in a
// neutral badge, rather than breaking the screen.
const UNKNOWN_DISPLAY = (value: string): StatusDisplay => ({ label: value, badge: 'locked' });

export const isKnownGiftStatus = (status: string): status is ScheduledGiftStatus =>
  Object.prototype.hasOwnProperty.call(GIFT_STATUS_DISPLAY, status);

export const getGiftStatusDisplay = (status: string): StatusDisplay =>
  isKnownGiftStatus(status) ? GIFT_STATUS_DISPLAY[status] : UNKNOWN_DISPLAY(status);

export const getRecipientOutcomeDisplay = (outcome: string): StatusDisplay =>
  Object.prototype.hasOwnProperty.call(RECIPIENT_OUTCOME_DISPLAY, outcome)
    ? RECIPIENT_OUTCOME_DISPLAY[outcome as ScheduledGiftRecipientOutcome]
    : UNKNOWN_DISPLAY(outcome);

export const getGiftSourceLabel = (source: string): string =>
  SOURCE_LABELS[source as ScheduledGiftSource] ?? source;

export const getOutcomeReasonLabel = (reason?: string): string | undefined =>
  reason ? OUTCOME_REASON_LABELS[reason] ?? reason : undefined;

export const getGiftStateReasonLabel = (reason?: string): string | undefined =>
  reason ? GIFT_STATE_REASON_LABELS[reason] ?? reason : undefined;

/** The message of a request the API refused as invalid (400), to show instead of a generic one. */
export const getRejectionMessage = (error: unknown): string | undefined => {
  if (axios.isAxiosError(error) && error.response?.status === 400) {
    return buildError(error, '') || undefined;
  }
  return undefined;
};

/**
 * What an admin can do with a gift, by its status. A status the screen doesn't know allows nothing.
 * Sending again is only for people whose payment failed: a skipped person (e.g. one who has left) was skipped on
 * purpose, so back office doesn't offer to send to them again.
 */
export const getGiftActions = (gift: ScheduledGift) => {
  const canChange = gift.status === 'SCHEDULED';

  return {
    canEdit: canChange,
    canCancel: canChange,
    canRetry: gift.status === 'FAILED' && gift.retryable,
    canSendAgain:
      isKnownGiftStatus(gift.status) &&
      gift.status !== 'SCHEDULED' &&
      gift.status !== 'PROCESSING' &&
      gift.status !== 'CANCELLED' &&
      gift.failedCount > 0,
  };
};

/** Every send time in a day, one slot every 15 minutes, as 'HH:mm'. */
export const getScheduleTimeSlots = (): string[] =>
  Array.from({ length: (24 * 60) / SCHEDULE_SLOT_MINUTES }, (_, index) => {
    const minutes = index * SCHEDULE_SLOT_MINUTES;
    const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
    return `${hours}:${String(minutes % 60).padStart(2, '0')}`;
  });

const getUkParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SCHEDULE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
};

// How far UK time is ahead of UTC at a given moment, in milliseconds: 0 in winter, 1 hour in summer.
const getUkOffset = (date: Date) => {
  const { year, month, day, hour, minute } = getUkParts(date);
  return Date.UTC(year, month - 1, day, hour, minute) - date.getTime();
};

/**
 * Reads a date from the date picker, which gives 'dd/MM/yyyy' while it is typed and 'yyyy-MM-dd' once a day
 * is picked or the field is left.
 */
const parseScheduleDate = (date?: string) => {
  const typed = date?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (typed) {
    return { day: Number(typed[1]), month: Number(typed[2]), year: Number(typed[3]) };
  }
  const picked = date?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (picked) {
    return { day: Number(picked[3]), month: Number(picked[2]), year: Number(picked[1]) };
  }
  return undefined;
};

/** Whether the date picker holds a whole date, in either of its formats. */
export const isCompleteScheduleDate = (date?: string): boolean => !!parseScheduleDate(date);

/** The date as the date picker shows it, 'dd/MM/yyyy', whichever format it arrived in. */
export const toDisplayScheduleDate = (date: string): string => {
  const parsed = parseScheduleDate(date);
  const pad = (value: number) => String(value).padStart(2, '0');
  return parsed ? `${pad(parsed.day)}/${pad(parsed.month)}/${parsed.year}` : date;
};

/**
 * Turns a UK date (either date picker format) and time ('HH:mm') into the UTC instant the API expects.
 * Returns undefined when either part is missing or not a real date.
 */
export const toScheduledAt = (date?: string, time?: string): string | undefined => {
  const parsedDate = parseScheduleDate(date);
  const timeMatch = time?.match(/^(\d{2}):(\d{2})$/);
  if (parsedDate && timeMatch) {
    const { day, month, year } = parsedDate;
    const [, hour, minute] = timeMatch.map(Number);
    const asIfUtc = Date.UTC(year, month - 1, day, hour, minute);
    const check = new Date(asIfUtc);
    if (check.getUTCDate() !== day || check.getUTCMonth() !== month - 1) {
      return undefined;
    }
    // Apply the offset in force at that moment, which differs either side of the clocks changing.
    const instant = asIfUtc - getUkOffset(new Date(asIfUtc - getUkOffset(new Date(asIfUtc))));
    return new Date(instant).toISOString();
  }
  return undefined;
};

/** Splits a UTC instant into the UK date ('dd/MM/yyyy') and time ('HH:mm') the form shows. */
export const fromScheduledAt = (scheduledAt: string): { date: string; time: string } => {
  const { year, month, day, hour, minute } = getUkParts(new Date(scheduledAt));
  const pad = (value: number) => String(value).padStart(2, '0');

  return { date: `${pad(day)}/${pad(month)}/${year}`, time: `${pad(hour)}:${pad(minute)}` };
};

/** e.g. 'Fri 7 Nov 2026, 09:00', in UK time. */
export const formatScheduledAt = (scheduledAt: string): string => {
  // Built from parts, since how Intl joins them differs between browsers.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SCHEDULE_TIME_ZONE,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).formatToParts(new Date(scheduledAt));
  const get = (type: string) => parts.find((part) => part.type === type)?.value;

  return `${get('weekday')} ${get('day')} ${get('month')} ${get('year')}, ${
    fromScheduledAt(scheduledAt).time
  }`;
};

/**
 * Today's date in the UK, as a local midnight for the date picker, which compares days in the browser's own time
 * zone. Just after midnight in the UK an admin abroad still on yesterday can then pick the UK's today, and not yet
 * tomorrow.
 */
export const getUkToday = (now: Date = new Date()): Date => {
  const { year, month, day } = getUkParts(now);
  return new Date(year, month - 1, day);
};

/**
 * Whether a time happens at all on a UK date. When the clocks go forward, 01:00 to 01:45 are skipped; saving one
 * would quietly send the gift an hour later, so they can't be picked. Times the clocks repeat in autumn are real.
 */
export const isRealUkTime = (date: string, time: string): boolean => {
  const scheduledAt = toScheduledAt(date, time);
  if (scheduledAt) {
    const shown = fromScheduledAt(scheduledAt);
    return shown.date === toDisplayScheduleDate(date) && shown.time === time;
  }
  return false;
};

/** Whether a slot on a UK date is still in the future, so it can be picked. */
export const isSlotInFuture = (date: string, time: string, now: Date = new Date()): boolean => {
  const scheduledAt = toScheduledAt(date, time);
  return !!scheduledAt && new Date(scheduledAt).getTime() > now.getTime();
};
